import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Waves, Building2, Camera, Euro, Star, CheckCircle2, ArrowRight } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1597221336986-7a948756cd3a?w=1600&q=80";

export default function HotelSignup() {
  const { t, register } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({ hotel_name: "", name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await register({ ...form, role: "owner" });
      toast.success(`Benvenuto ${user.name}!`);
      // Route to Create Listing so they can add their first pool immediately
      nav("/owner/new");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] relative">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 px-6 py-12 items-start">
        {/* Left: pitch */}
        <div>
          <div className="overline text-[#E27B58] mb-4">{t("hotelSignup.badge")}</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold leading-tight mb-4">{t("hotelSignup.title")}</h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">{t("hotelSignup.subtitle")}</p>
          <div className="rounded-3xl overflow-hidden h-64 mb-6 relative">
            <img src={IMG} alt="Pool" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur px-4 py-2 rounded-full text-xs font-semibold text-[#005C7A] flex items-center gap-2">
              <Star className="w-3.5 h-3.5 fill-[#E27B58] text-[#E27B58]" /> {t("hotelSignup.trustBadge")}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { Icon: Building2, t: t("hotelSignup.f1t"), d: t("hotelSignup.f1d") },
              { Icon: Camera, t: t("hotelSignup.f2t"), d: t("hotelSignup.f2d") },
              { Icon: Euro, t: t("hotelSignup.f3t"), d: t("hotelSignup.f3d") },
              { Icon: CheckCircle2, t: t("hotelSignup.f4t"), d: t("hotelSignup.f4d") },
            ].map((f, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-4">
                <f.Icon className="w-5 h-5 text-[#005C7A] mb-2" />
                <div className="font-semibold text-sm mb-0.5">{f.t}</div>
                <div className="text-xs text-muted-foreground">{f.d}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: form */}
        <div className="bg-white border border-border rounded-3xl p-8 lg:sticky lg:top-24 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-full bg-[#005C7A] flex items-center justify-center"><Waves className="w-5 h-5 text-white" /></div>
            <span className="font-display text-lg font-bold text-[#005C7A]">PoolPass · Hotels</span>
          </div>
          <h2 className="font-display text-2xl font-bold mb-1">{t("hotelSignup.formTitle")}</h2>
          <p className="text-sm text-muted-foreground mb-4">{t("hotelSignup.formSubtitle")}</p>
          <div className="bg-[#005C7A]/5 border-l-4 border-[#005C7A] rounded-r-lg p-3 mb-5 text-xs leading-relaxed text-[#1A202C]" data-testid="fee-disclosure">
            <strong>Lancio Iniziale · Zero commissioni.</strong> Registrati in 60 secondi: nessun conto di pagamento da collegare. Le prenotazioni sono gratuite per il cliente e vengono saldate <strong>direttamente in struttura</strong> (contanti o carta al POS). Tu ricevi solo prenotazioni confermate.
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>{t("hotelSignup.hotelName")}</Label>
              <Input required value={form.hotel_name} onChange={(e) => setForm({ ...form, hotel_name: e.target.value })} placeholder="Hotel Belvedere" data-testid="hs-hotel-name" />
            </div>
            <div>
              <Label>{t("hotelSignup.contactName")}</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Marco Rossi" data-testid="hs-contact-name" />
            </div>
            <div>
              <Label>{t("auth.email")}</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="hs-email" />
            </div>
            <div>
              <Label>{t("auth.password")}</Label>
              <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="hs-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#005C7A] hover:bg-[#004A63] h-12" data-testid="hs-submit">
              {busy ? "..." : <>{t("hotelSignup.cta")} <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              {t("auth.hasAccount")} <Link to="/auth" className="text-[#005C7A] font-semibold hover:underline">{t("auth.signin")}</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
